import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';

// Small iOS-style switch (the prebuilt output.css lacks peer/toggle utilities,
// so the track + knob are inline-styled).
function OutlierSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative', width: 34, height: 20, borderRadius: 9999, flexShrink: 0,
        border: 'none', cursor: 'pointer', padding: 0, transition: 'background-color 0.15s',
        backgroundColor: checked ? '#0891b2' : '#cbd5e1'
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: checked ? 16 : 2, width: 16, height: 16,
        borderRadius: 9999, background: '#fff', transition: 'left 0.15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
      }} />
    </button>
  );
}

const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${v} %`;
const fmtDurationDefault = (sec) => {
  if (sec === null || sec === undefined) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} min` : `${s} s`;
};

const COLUMNS = [
  { key: 'title', label: 'Vorlage', numeric: false },
  { key: 'count', label: 'Ergebnisse', numeric: true },
  { key: 'avgScorePct', label: 'Ø-Score', numeric: true },
  { key: 'recommendedPct', label: '% Empfohlen', numeric: true },
  { key: 'medianDurationSec', label: 'Median-Zeit', numeric: true },
  { key: 'avgDurationSec', label: 'Ø-Zeit', numeric: true }
];

function RowBreakdown({ row, formatDuration }) {
  const split = Array.isArray(row.split) ? row.split : [];
  const hasScored = split.some(s => s.count > 0);
  const stats = [
    { label: 'Ergebnisse', value: row.count },
    { label: 'Ø-Score', value: fmtPct(row.avgScorePct) },
    { label: '% Empfohlen', value: fmtPct(row.recommendedPct) },
    { label: 'Min-Zeit', value: formatDuration(row.minDurationSec) },
    { label: 'Median-Zeit', value: formatDuration(row.medianDurationSec) },
    { label: 'Ø-Zeit', value: formatDuration(row.avgDurationSec) },
    { label: 'Max-Zeit', value: formatDuration(row.maxDurationSec) }
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5rem', padding: '0.5rem 0 1rem' }}>
      {hasScored ? (
        <div style={{ width: 150, height: 150, flexShrink: 0 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={split} dataKey="count" nameKey="label" innerRadius={38} outerRadius={68} paddingAngle={2}>
                {split.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v, name) => [`${v}`, name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-xs text-slate-400" style={{ width: 150, textAlign: 'center' }}>Noch keine ausgewerteten Ergebnisse.</div>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          {split.map(s => (
            <span key={s.label} className="text-xs text-slate-600 dark:text-slate-300" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: 9, height: 9, borderRadius: 9999, background: s.color, flexShrink: 0 }} />
              {s.label}: <b>{s.count}</b> ({s.pct}%)
            </span>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px' }}>
          {stats.map(st => (
            <div key={st.label}>
              <div className="text-[11px] text-slate-400">{st.label}</div>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{st.value}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-400" style={{ marginTop: '8px' }}>
          {row.evaluatedCount} von {row.count} ausgewertet
          {row.ignoredCount > 0 && ` · ${row.ignoredCount} Zeit${row.ignoredCount === 1 ? '' : 'en'} über 2 h ignoriert`}
        </div>
      </div>
    </div>
  );
}

export function QuestionnaireComparisonTable({ rows, formatDuration = fmtDurationDefault, ignoreOutliers = true, onToggleOutliers }) {
  const [sortKey, setSortKey] = useState('count');
  const [dir, setDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [rows, sortKey, dir]);

  const onSort = (key) => {
    if (key === sortKey) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setDir('desc'); }
  };

  if (rows.length === 0) {
    return <div className="text-sm text-slate-400 py-8 text-center">Keine Fragebögen mit Ergebnissen im Zeitraum.</div>;
  }

  return (
    <div>
      {onToggleOutliers && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="text-xs text-slate-500 dark:text-slate-400">Zeitausreißer über 2&nbsp;h ignorieren</span>
          <span
            title="Ein zeitweise fehlerhafter Timer hat einzelne Bearbeitungszeiten in Stundenhöhe erfasst. Diese werden aus Median-, Ø-, Min- und Max-Zeit herausgerechnet."
            style={{ display: 'inline-flex', color: '#94a3b8', cursor: 'help' }}
          >
            <Info size={13} aria-hidden="true" />
          </span>
          <OutlierSwitch checked={ignoreOutliers} onChange={onToggleOutliers} />
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
            {COLUMNS.map(col => (
              <th key={col.key} className={`py-2 px-3 ${col.numeric ? 'text-right' : ''}`}>
                <button type="button" onClick={() => onSort(col.key)} className="inline-flex items-center gap-1 font-medium hover:text-slate-700 dark:hover:text-slate-200">
                  {col.label}{sortKey === col.key ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => (
            <React.Fragment key={row.id}>
              <tr
                onClick={() => setExpandedId(id => id === row.id ? null : row.id)}
                className="border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                title="Für Details klicken"
              >
                <td className="py-2 px-3">
                  <span className="text-slate-400 mr-1" style={{ display: 'inline-block', transform: expandedId === row.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▸</span>
                  {row.title}
                </td>
                <td className="py-2 px-3 text-right">{row.count}</td>
                <td className="py-2 px-3 text-right">{fmtPct(row.avgScorePct)}</td>
                <td className="py-2 px-3 text-right">{fmtPct(row.recommendedPct)}</td>
                <td className="py-2 px-3 text-right">{formatDuration(row.medianDurationSec)}</td>
                <td className="py-2 px-3 text-right">{formatDuration(row.avgDurationSec)}</td>
              </tr>
              {expandedId === row.id && (
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30">
                  <td colSpan={COLUMNS.length} className="px-3">
                    <RowBreakdown row={row} formatDuration={formatDuration} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
